import { createRef, useState } from 'react'
import router from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'
import {
  anyPass,
  equals,
  groupBy,
  head,
  isEmpty,
  isNil,
  map,
  path,
  pipe,
  prop,
  values as ramdaValues,
  toPairs
} from 'ramda'
import {
  Box,
  Text,
  TextLink,
  useDialogController
} from '@machobear/design-system'
import { addMinutes } from 'date-fns'
import { useDispatch, useSelector } from 'react-redux'
import Slider from 'react-slick'

import ENV_VARS from '@/config'
import { tenantStartChatNoMoveInDate } from '@/ports'
import { colors, fontSizes, fontWeights } from '@/styles'
import { formatDate } from '@/utils/date'
import { show as showLoginModal } from '@/features/Auth/login-modal'
import { Button } from '@/components/button-new'
import { WarningModal } from '@/components/ds-warning-modal'
import { Input } from '@/components/form'
import { createModal } from '@/components/modalize'
import { toast } from '@/components/toast-notifications'

import { ReactComponent as ButtonLeftIcon } from './button-left.svg'
import { ReactComponent as ButtonRightIcon } from './button-right.svg'

const { DASHBOARD_URL } = ENV_VARS

const isFalsy = anyPass([isNil, isEmpty, equals(false)])

const Padding = styled.div`
  height: ${props => (props.height ? props.height : 'auto')};
  display: ${props => (props.isFlex ? 'flex' : 'auto')};
  flex-direction: ${props =>
    props.flexDirection ? props.flexDirection : 'unset'};
  justify-content: ${props =>
    props.justifyContent ? props.justifyContent : 'unset'};
  align-items: ${props => (props.alignItems ? props.alignItems : 'unset')};
  padding: ${props =>
    props.customPadding ? props.customPadding : '30px 24px'};
`

const ShowingsButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  background: ${colors.grey96};
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding: 24px;
`

const ModalTitle = styled.p`
  font-size: ${props => (props.fontSize ? props.fontSize : fontSizes.large)};
  text-align: left;
  font-weight: 700;
  width: ${props => (props.width ? props.width : 'auto')};
  margin-top: ${({ top = 0 }) => (top > 0 ? `${top}px` : 0)};
  margin-bottom: ${({ bottom = 0 }) => (bottom > 0 ? `${bottom}px` : 0)};
  padding: ${props => (props.padding ? props.padding : '0')};
  border-bottom: ${props =>
    props.hasBorderBottom ? `1px solid ${colors.grey96}` : 'none'};
`

const StyledButton = styled(Button)`
  padding: 0;
`
const Appointments = styled.div`
  .slick-slider .slick-next,
  .slick-slider .slick-prev {
    top: 0;
    transform: translate(0, 0);
  }

  .slick-slider .slick-next:before,
  .slick-slider .slick-prev:before {
    color: rgba(0, 0, 0, 0.6);
  }
`
const AppointmentHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`
const AppointmentDay = styled.div`
  outline: 0;
`

const AppointmentText = styled.p`
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.medium};
  text-align: center;
  margin: 0 16px;
`

const AppointmentsBox = styled.div`
  max-height: 400px;
  overflow: auto;
  margin: 30px 0;
`

const Appointment = styled.div`
  border: 1px solid #ccc;
  border-bottom: none;
  display: flex;
  align-items: center;
  height: 70px;
  padding: 0 20px;

  &:first-child {
    border-radius: 5px 5px 0 0;
  }
  &:last-child {
    border-radius: 0 0 5px 5px;
    border-bottom: 1px solid #ccc;
  }
  &:first-child:last-child {
    border-radius: 5px;
  }
`

const Time = styled.div`
  color: ${colors.grey16};
  font-size: ${fontSizes.regular};
  line-height: 24px;
  font-weight: 500;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;

  margin-left: 16px;
`

const Duration = styled.div`
  color: ${colors.blue24};
  font-size: 12px;
  line-height: 16px;
  padding: 8px 12px;
  margin-left: auto;
  background: ${colors.blue96};
  border-radius: 8px;
`

const Content = styled.div`
  padding: 0 24px;
`
const parseErrors = body => body?.message || ramdaValues(body?.fields || body)

const RadioInput = styled(Input)`
  width: 24px;
  height: 24px;
`
const ShowingsTopContainer = styled.div``

const AppointmentForm = ({
  listingId,
  landlordName,
  appointments = [],
  selectedAppointment = null
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const bookViewing = dispatch.listing.bookViewing
  const logout = dispatch.auth.logout
  const session = useSelector(path(['session']))
  const [appointment, setAppointment] = useState(null)
  const { visible, showModal, closeModal } = useDialogController(false)
  const goToChatroom = timelineId => {
    window.location.href = `${DASHBOARD_URL}/chat/${timelineId || ''}`
  }

  const showErrorNotification = errors => {
    const title = t(
      'listing:b.appointment.modal.showing_booking_failed.title',
      'Showing Booking Failed'
    )
    toast(errors, {
      title,
      type: 'error'
    })
  }

  const handleLogin = (session, currentStep) => {
    if (
      (currentStep === 'register' || currentStep === 'registerTenant') &&
      session?.user_type === 'tenant'
    ) {
      handleBookAppointment()
    } else {
      show({
        appointments,
        selectedAppointment: appointment,
        listingId,
        landlordName
      })
    }
  }

  const handleBookAppointment = async () => {
    dispatch.loading.setLoading()
    const slotId = selectedAppointment?.id || appointment.id

    const chatResponse = await tenantStartChatNoMoveInDate(
      {
        message: 'Viewing booked!'
      },
      {
        listingId
      }
    )

    if (chatResponse.response.ok) {
      const timelineId = pipe(
        map(prop('timeline_room')),
        ramdaValues,
        head
      )(chatResponse.body.data)
      const response = await bookViewing({ listingId, timeslotId: slotId })
      if (response.ok) {
        goToChatroom(timelineId)
        dispatch.loading.stopLoading()
      } else {
        dispatch.loading.stopLoading()
        const errors = parseErrors(response?.error || response.body)
        showErrorNotification(errors)
      }
    } else {
      dispatch.loading.stopLoading()
      const errors = parseErrors(chatResponse?.error || chatResponse.body)
      showErrorNotification(errors)
    }
  }
  const handleSelectAppointment = () => {
    const isLandlord = session?.user_type === 'landlord'
    const registrationComplete = session?.registration_complete === '1'
    const isLoggedIn = !!session?.id && registrationComplete
    if (isLandlord) {
      showModal()
    } else if (isLoggedIn) {
      handleBookAppointment()
    } else {
      showLoginModal({
        mode: 'registerTenant',
        title: t(
          'listing:b.appointment.modal.book_showing.title',
          'Book Showing'
        ),
        afterLogin: handleLogin,
        t
      })
    }
  }
  const renderSelectedAppointment = () => {
    return (
      <Appointments>
        <AppointmentDay>
          <AppointmentText>
            {formatDate(
              selectedAppointment.meeting_at,
              'MMMM do, yyyy',
              router.locale
            )}
          </AppointmentText>
          <AppointmentsBox>
            <Appointment key={selectedAppointment.id}>
              <Time>
                {formatDate(
                  selectedAppointment.meeting_at,
                  'h:mm a',
                  router.locale
                )}
              </Time>
              <Duration>
                <strong>{selectedAppointment.duration}</strong> min
              </Duration>
              <RadioInput
                type="radio"
                name="appointment"
                checked={true}
                readOnly
              />
            </Appointment>
          </AppointmentsBox>
        </AppointmentDay>
      </Appointments>
    )
  }

  const renderAppointments = () => {
    const dates = pipe(
      groupBy(appointment =>
        formatDate(appointment.meeting_at, 'MMMM do, yyyy', router.locale)
      ),
      toPairs
    )(appointments)
    const slider = createRef(null)
    return (
      <Appointments>
        {dates.length > 0 && (
          <Slider
            centerMode={false}
            infinite={dates.length > 1}
            slidesToShow={1}
            arrows={false}
            ref={slider}
          >
            {dates.map(([date, aps]) => (
              <AppointmentDay key={date}>
                <AppointmentHeader>
                  <StyledButton
                    theme="text"
                    size="unset"
                    onClick={() => slider?.current?.slickPrev()}
                  >
                    <ButtonLeftIcon />
                  </StyledButton>
                  <AppointmentText>{date}</AppointmentText>
                  <StyledButton
                    theme="text"
                    size="unset"
                    onClick={() => slider?.current?.slickNext()}
                  >
                    <ButtonRightIcon />
                  </StyledButton>
                </AppointmentHeader>
                <AppointmentsBox>
                  {aps.map(appointment => {
                    const meetingStartTime = formatDate(
                      appointment.meeting_at,
                      'h:mm a',
                      router.locale
                    )
                    const meetingEndTime = formatDate(
                      addMinutes(
                        new Date(appointment.meeting_at),
                        appointment.duration
                      ),
                      'h:mm a',
                      router.locale
                    )

                    return (
                      <Appointment key={appointment.id}>
                        <RadioInput
                          type="radio"
                          name="appointment"
                          onChange={() => setAppointment(appointment)}
                        />
                        <Time>{`${meetingStartTime} - ${meetingEndTime}`}</Time>
                        <Duration>
                          <strong>{appointment.duration}</strong> min
                        </Duration>
                      </Appointment>
                    )
                  })}
                </AppointmentsBox>
              </AppointmentDay>
            ))}
          </Slider>
        )}
      </Appointments>
    )
  }

  const disableBook = isFalsy(appointment) && isFalsy(selectedAppointment)

  return (
    <>
      <Padding
        height={'100%'}
        isFlex
        customPadding={'0px'}
        flexDirection={'column'}
        justifyContent={'space-between'}
      >
        <ShowingsTopContainer>
          <ModalTitle
            padding={'24px'}
            width={'100%'}
            bottom={20}
            fontSize={'16px'}
            hasBorderBottom
          >
            {t(
              'listing:b.appointment.modal.available_showings.title',
              'Available Showings'
            )}
          </ModalTitle>
          <Content>
            {selectedAppointment
              ? renderSelectedAppointment()
              : renderAppointments()}
          </Content>
        </ShowingsTopContainer>

        <ShowingsButtonWrapper>
          <Button
            theme="primary"
            onClick={handleSelectAppointment}
            disabled={disableBook}
            width={'114px'}
          >
            {t('listing:b.appointment.modal.book.button', 'Book')}
          </Button>
        </ShowingsButtonWrapper>
      </Padding>
      <WarningModal
        visible={visible}
        closeModal={closeModal}
        title={t(
          'b.listing.view.logged_in_as_landlord.title',
          "Oops! It looks like you're logged in as a landlord."
        )}
        description={
          <Box marginTop="small">
            <Text tone="secondary" align="center" size="medium">
              <Trans i18nKey="b.listing.view.logged_in_as_landlord.text">
                To access renter-specific features, please log out and sign up
                for a renter account or log in with your renter account details.
                <br />
                <br />
                If you made this account in error, you can contact us at{' '}
                <TextLink
                  href="mailto:support@liv.rent"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  support@liv.rent
                </TextLink>
                .
              </Trans>
            </Text>
          </Box>
        }
        button={{
          text: t('common.log_out', 'Log Out'),
          onClick: () => {
            logout()
            closeModal()
          }
        }}
      />
    </>
  )
}

export const show = createModal(AppointmentForm, {
  width: 435,
  height: '100%',
  closeButtonTop: '24px',
  closeButtonRight: '21px'
})
